/**
 * Shared DOJ/DOL vs attendance-lock validation (Org Details + Resignation modal).
 */

export const LOCKED_ATTENDANCE_DATE_TOAST =
  "Unlock those month(s) in Attendance, then update Date of Join or Relieving Date.";

export function parseDateSafe(value) {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
}

export function toMonthKey(dateValue) {
  return dateValue.getFullYear() * 12 + (dateValue.getMonth() + 1);
}

export function getMonthLabel(month, year) {
  return new Date(Number(year), Number(month) - 1, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
}

/**
 * @param {object} params
 * @param {string|Date|null} params.oldJoiningDate
 * @param {string|Date|null} params.oldRelievedDate
 * @param {string|Date|null} params.newJoiningDate
 * @param {string|Date|null} params.newRelievedDate
 * @param {Array<{ month: number|string, year: number|string }>} params.lockRows
 * @returns {{ blocked: boolean, lockedMonths: Array<{ month: number, year: number, label: string }> }}
 */
export function getLockedMonthsForEmploymentDateChange({
  oldJoiningDate,
  oldRelievedDate,
  newJoiningDate,
  newRelievedDate,
  lockRows = [],
}) {
  const oldJoin = parseDateSafe(oldJoiningDate);
  const oldRelieve = parseDateSafe(oldRelievedDate);
  const newJoin = parseDateSafe(newJoiningDate);
  const newRelieve = parseDateSafe(newRelievedDate);

  const joiningChanged =
    oldJoin && newJoin
      ? oldJoin.getTime() !== newJoin.getTime()
      : oldJoin !== newJoin;
  const relievedChanged =
    oldRelieve && newRelieve
      ? oldRelieve.getTime() !== newRelieve.getTime()
      : oldRelieve !== newRelieve;

  if (!joiningChanged && !relievedChanged) {
    return { blocked: false, lockedMonths: [] };
  }

  const oldStartKey = oldJoin ? toMonthKey(oldJoin) : null;
  const oldEndKey = oldRelieve ? toMonthKey(oldRelieve) : null;
  const newStartKey = newJoin ? toMonthKey(newJoin) : null;
  const newEndKey = newRelieve ? toMonthKey(newRelieve) : null;

  const inWindow = (lockKey, startKey, endKey) => {
    if (startKey == null) return false;
    if (lockKey < startKey) return false;
    if (endKey != null && lockKey > endKey) return false;
    return true;
  };

  const lockedMonths = (Array.isArray(lockRows) ? lockRows : [])
    .filter((lock) => {
      const m = Number(lock.month);
      const y = Number(lock.year);
      if (!m || !y) return false;
      const lockKey = y * 12 + m;
      const inOld = inWindow(lockKey, oldStartKey, oldEndKey);
      const inNew = inWindow(lockKey, newStartKey, newEndKey);
      const changedByRangeMembership = inOld !== inNew;

      const impactedOldRelievingMonth =
        relievedChanged && oldEndKey != null && lockKey === oldEndKey;
      const impactedNewRelievingMonth =
        relievedChanged && newEndKey != null && lockKey === newEndKey;
      const impactedOldJoiningMonth =
        joiningChanged && oldStartKey != null && lockKey === oldStartKey;
      const impactedNewJoiningMonth =
        joiningChanged && newStartKey != null && lockKey === newStartKey;

      return (
        changedByRangeMembership ||
        impactedOldRelievingMonth ||
        impactedNewRelievingMonth ||
        impactedOldJoiningMonth ||
        impactedNewJoiningMonth
      );
    })
    .map((lock) => ({
      month: Number(lock.month),
      year: Number(lock.year),
      label: getMonthLabel(lock.month, lock.year),
    }))
    .sort((a, b) => a.year - b.year || a.month - b.month);

  return {
    blocked: lockedMonths.length > 0,
    lockedMonths,
  };
}

/**
 * @param {object} params - same date fields as getLockedMonthsForEmploymentDateChange
 * @param {() => Promise<{ data?: Array }>} params.fetchAttendanceLocks
 */
export async function checkLockedAttendanceForEmploymentDateChange({
  oldJoiningDate,
  oldRelievedDate,
  newJoiningDate,
  newRelievedDate,
  fetchAttendanceLocks,
}) {
  try {
    const locksRes = await fetchAttendanceLocks();
    const lockRows = Array.isArray(locksRes?.data) ? locksRes.data : [];
    return getLockedMonthsForEmploymentDateChange({
      oldJoiningDate,
      oldRelievedDate,
      newJoiningDate,
      newRelievedDate,
      lockRows,
    });
  } catch (e) {
    console.warn("Failed to fetch attendance locks for date validation:", e);
    return { blocked: false, lockedMonths: [] };
  }
}

export function buildLockedAttendanceDateToast(lockedMonths) {
  const labels = lockedMonths.map((m) => m.label).join(", ");
  return `Attendance is locked for: ${labels}. ${LOCKED_ATTENDANCE_DATE_TOAST}`;
}
